import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Radar,
  TriangleAlert,
  SlidersHorizontal,
  Plus,
  LogOut,
} from "lucide-react";
import { clsx } from "clsx";
import { Logo } from "./components/ui";
import { useAuth } from "./lib/auth";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import Scans from "./pages/Scans";
import Findings from "./pages/Findings";
import ScanDetail from "./pages/ScanDetail";
import SettingsPage from "./pages/Settings";

type NavItem = { to: string; icon: typeof Radar; label: string; end?: boolean };
type NavGroup = { label: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  { label: "Overview", items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard", end: true }] },
  {
    label: "Workspace",
    items: [
      { to: "/projects", icon: FolderKanban, label: "Projects" },
      { to: "/scans", icon: Radar, label: "Scans" },
      { to: "/findings", icon: TriangleAlert, label: "Findings" },
    ],
  },
  { label: "System", items: [{ to: "/settings", icon: SlidersHorizontal, label: "Settings" }] },
];

function useApiStatus() {
  const [status, setStatus] = useState<"online" | "offline" | "checking">("checking");
  useEffect(() => {
    const base = import.meta.env.VITE_API_URL || "/api";
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    fetch(`${base}/health`, { signal: controller.signal })
      .then((r) => setStatus(r.ok ? "online" : "offline"))
      .catch(() => setStatus("offline"))
      .finally(() => clearTimeout(timer));
    return () => clearTimeout(timer);
  }, []);
  return status;
}

export default function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const apiStatus = useApiStatus();
  const { isAuthenticated, logout } = useAuth();

  const pageTitle =
    NAV_GROUPS.flatMap((g) => g.items).find((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to)))
      ?.label ?? "Petir";

  if (!isAuthenticated) {
    return (
      <>
        <div className="app-backdrop" />
        <div className="app-grid" />
        <Login />
      </>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="app-backdrop" />
      <div className="app-grid" />

      {/* Sidebar */}
      <aside className="flex w-60 shrink-0 flex-col border-r border-line bg-ink-900/70 backdrop-blur">
        <div className="flex h-16 items-center px-5">
          <Logo />
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-5">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-mist-600">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map(({ to, icon: Icon, label, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      clsx(
                        "group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-ink-800 text-mist-100"
                          : "text-mist-400 hover:bg-ink-800/60 hover:text-mist-200",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={clsx(
                            "absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-volt-400 transition-opacity",
                            isActive ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <Icon className={clsx("h-[18px] w-[18px]", isActive ? "text-volt-400" : "text-mist-500 group-hover:text-mist-300")} />
                        {label}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-line px-4 py-4">
          <div className="flex items-center gap-2.5">
            <span
              className={clsx(
                "h-2 w-2 rounded-full",
                apiStatus === "online" && "bg-emerald-400",
                apiStatus === "offline" && "bg-red-400",
                apiStatus === "checking" && "bg-mist-500 animate-pulse",
              )}
            />
            <div className="text-xs">
              <p className="font-medium text-mist-300">
                {apiStatus === "online" ? "API online" : apiStatus === "offline" ? "API offline" : "Checking…"}
              </p>
              <p className="text-mist-600">v0.1.0</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-line bg-ink-900/50 px-6 backdrop-blur">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-mist-500">Petir</span>
            <span className="text-mist-600">/</span>
            <span className="font-medium text-mist-100">{pageTitle}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/scans", { state: { openNew: true } })} className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4" /> New scan
            </button>
            <button onClick={logout} className="btn btn-ghost btn-sm" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-6 py-7">
          <div className="mx-auto max-w-6xl animate-fade">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/scans" element={<Scans />} />
              <Route path="/scans/:scanId" element={<ScanDetail />} />
              <Route path="/findings" element={<Findings />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}
